// Top-level transcripts (founder-ratified shell IA, 2026-07-07) — ONE List
// screen for both scopes (transcripts.md, UI Layering Sweep Phase 3). With an
// agent scoped in the sidebar selector this is the rich single-agent
// workspace (upload drawer + connection check); with no agent it is the real
// team-wide list — the List call simply omits `agent_id`, which
// `core.list_transcripts` scopes to the caller's organization (never a
// client-side fan-out). Live updates ride the shell's app-wide SSE
// subscription (docs/09 §5).
// `agent_id` is always a query filter, never a path parent. URL state
// (`?status/source/batch/upload` + date ranges) is owned by the route's
// validateSearch (lib/routerSearch.parseTranscriptsSearch); every search
// write is a functional merge so the root `?agent` scope survives.

import { useMemo } from "react";

import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "@tanstack/react-router";

import { useResolvedWorkingTeamId, useWorkingScope } from "../lib/workingScope";
import { apiClient } from "../lib/apiClient";
import { ApiRequestError } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { isShowcaseMode } from "../lib/showcaseMode";
import {
  parseTranscriptsSearch,
  transcriptListReturnSearch,
  type TranscriptsSearch,
} from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { useListQuery } from "../lib/useListQuery";
import { Button, FilterBar, Page, PageHeader, type FilterValue } from "../ui";
import {
  CLEARED_TRANSCRIPT_FACETS,
  TRANSCRIPT_FACETS,
  hasActiveTranscriptFacets,
  transcriptFacetSearch,
  transcriptFacetValue,
  transcriptFiltersToQueryKey,
  transcriptListFilters,
} from "./transcripts/transcriptFacets";
import {
  BatchRollupStrip,
  type BatchStatusCounts,
} from "./transcripts/BatchRollupStrip";
import { TranscriptsEmptyState } from "./transcripts/TranscriptsEmptyState";
import { TranscriptsList } from "./transcripts/TranscriptsList";
import { TranscriptUploadDrawer } from "./transcripts/TranscriptUploadDrawer";
import { useTranscriptUpload } from "./transcripts/useTranscriptUpload";
import { ShowcaseReadOnlyNotice } from "./ShowcaseReadOnlyNotice";

export function Transcripts() {
  const { agentId } = useWorkingScope();
  const workingTeamId = useResolvedWorkingTeamId();
  const navigate = useTypedNavigate<TranscriptsSearch>();
  // Cross-route open: the transcript detail is a full-page dossier route, so a
  // row click navigates there (preserving the root-owned working scope), rather
  // than opening a drawer like markers/personas.
  const openNavigate = useNavigate();
  const search = useTypedSearch(parseTranscriptsSearch);

  function openTranscript(id: string): void {
    void openNavigate({
      params: { id },
      search: transcriptListReturnSearch,
      to: "/transcripts/$id",
    });
  }

  // Same-route search writes are ALWAYS functional merges (transcripts.md),
  // typed through the shared router helper.
  function patchSearch(patch: TranscriptsSearch, replace: boolean): void {
    void navigate({
      replace,
      search: mergeSearch<TranscriptsSearch>(patch),
    });
  }

  // The API's own filter shapes plus the applied FilterBar value, both derived
  // from the URL (§4c) — see transcripts/transcriptFacets.
  const filters = transcriptListFilters(search);
  const hasActiveFilters = hasActiveTranscriptFacets(filters);
  const facets = useMemo<FilterValue>(
    () => transcriptFacetValue(search),
    [search],
  );

  // Filter writes are replaces so tweaking a facet never fills the Back stack.
  // The batch facet survives: the rollup strip owns its own dismissal.
  function applyFacets(next: FilterValue): void {
    patchSearch(transcriptFacetSearch(next), true);
  }

  // Filters ride inside the key: a filter change is a fresh keyset stream,
  // never an offset synthesis (docs/09 §6).
  // All agents = the working team's data (docs/09 §4); held until resolved.
  const transcripts = useListQuery("/v1/transcripts", {
    enabled: agentId != null || workingTeamId !== undefined,
    params:
      agentId != null
        ? { ...filters, agent_id: agentId }
        : { ...filters, team_id: workingTeamId },
    queryKey:
      agentId != null
        ? queryKeys.transcripts.list({
            agent_id: agentId,
            ...transcriptFiltersToQueryKey(filters),
          })
        : queryKeys.transcripts.list({
            team_id: workingTeamId,
            ...transcriptFiltersToQueryKey(filters),
          }),
  });

  // `?batch=` also mounts the rollup strip off the batch detail read.
  const batchId = search.batch;
  const batchDetail = useQuery({
    enabled: batchId != null,
    queryKey: queryKeys.batches.detail(batchId ?? ""),
    queryFn: async () => {
      const { data, error } = await apiClient.GET("/v1/batches/{id}", {
        params: { path: { id: batchId ?? "" } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  // Live updates arrive via the shell's single app-wide SSE subscription
  // (AppLayout + lib/sse.ts useShellStream, docs/09 §5); `batch.updated` →
  // batches.all keeps the rollup strip live without a screen-local stream.

  const upload = useTranscriptUpload(agentId);
  // Upload requires agent_id: validateSearch keeps `?upload=1` unscoped, the
  // container ignores it here.
  const isUploadOpen = search.upload === 1 && agentId != null;

  const loadedCounts: BatchStatusCounts = useMemo(() => {
    const counts = {
      failed: 0,
      pending: 0,
      processing: 0,
      ready: 0,
      total: transcripts.items.length,
    };
    for (const transcript of transcripts.items) {
      counts[transcript.status] += 1;
    }
    return counts;
  }, [transcripts.items]);

  // First-run detection (docs/09 §4): with no agent scope and the unfiltered
  // team-wide list resolved empty, ONE tiny probe answers "does the org have
  // any agent at all?" — an empty first page of GET /v1/agents with no
  // filters. Zero agents ⇒ the front door renders the first-run spine in
  // place of the team-wide empty state; the probe never fires otherwise.
  const mayBeFirstRun =
    agentId == null &&
    !hasActiveFilters &&
    !transcripts.isPending &&
    transcripts.errorMessage === undefined &&
    transcripts.items.length === 0;
  const agentsProbe = useListQuery("/v1/agents", {
    enabled: mayBeFirstRun,
    pageSize: 1,
    queryKey: queryKeys.agents.list({ surface: "first-run-probe" }),
  });
  const hasZeroAgents =
    mayBeFirstRun &&
    !agentsProbe.isPending &&
    agentsProbe.errorMessage === undefined &&
    agentsProbe.items.length === 0;

  // Team-wide Agent column: resolve agent ids to display names so the column
  // leads with the agent name and reveals the id on hover. Only the team-wide
  // list shows the column, so this is gated to `agentId == null` (agent scope
  // never probes agents). Reads the canonical `/v1/agents` list through the
  // shared keyset hook so it shares ONE cache entry with the shell selector
  // and the palette — the names are already loaded, no extra fetch.
  const agentsForNames = useListQuery("/v1/agents", {
    enabled: agentId == null,
    queryKey: queryKeys.agents.list(),
  });
  const agentNamesById = useMemo(
    () =>
      new Map(
        agentsForNames.items.map((agent) => [agent.id, agent.display_name]),
      ),
    [agentsForNames.items],
  );

  const emptyState = (
    <TranscriptsEmptyState
      agentId={agentId ?? undefined}
      hasActiveFilters={hasActiveFilters}
      hasZeroAgents={hasZeroAgents}
      isFirstRunProbePending={mayBeFirstRun && agentsProbe.isPending}
      onClearFilters={() => {
        // Filtered-empty needs a way back that also drops the batch narrowing.
        patchSearch({ ...CLEARED_TRANSCRIPT_FACETS, batch: undefined }, true);
      }}
    />
  );

  return (
    <Page variant="workspace">
      <PageHeader
        compact
        actions={
          isShowcaseMode ? (
            <ShowcaseReadOnlyNotice />
          ) : agentId != null ? (
            <Button
              size="sm"
              onClick={() => {
                patchSearch({ upload: 1 }, false);
              }}
            >
              Upload transcript
            </Button>
          ) : undefined
        }
        leading={
          <FilterBar
            facets={TRANSCRIPT_FACETS}
            onChange={applyFacets}
            value={facets}
          />
        }
      />
      <TranscriptsList
        agentNamesById={agentNamesById}
        batchStrip={
          batchId != null ? (
            <BatchRollupStrip
              batch={batchDetail.data}
              errorMessage={
                batchDetail.isError ? batchDetail.error.message : undefined
              }
              isPending={batchDetail.isPending}
              loadedCounts={loadedCounts}
              onClearBatch={() => {
                patchSearch({ batch: undefined }, true);
              }}
            />
          ) : null
        }
        emptyState={emptyState}
        errorMessage={transcripts.errorMessage}
        hasNextPage={transcripts.hasNextPage}
        isFetchingNextPage={transcripts.isFetchingNextPage}
        isPending={transcripts.isPending}
        items={transcripts.items}
        listStateKey={transcripts.listStateKey}
        showAgentColumn={agentId == null}
        onLoadMore={transcripts.fetchNextPage}
        onOpenTranscript={openTranscript}
      />
      <TranscriptUploadDrawer
        isOpen={isUploadOpen}
        onClose={() => {
          patchSearch({ upload: undefined }, true);
        }}
        onSubmit={upload.submit}
      />
    </Page>
  );
}
